import path from 'path';

import WeekdayEnum from '~enums/dayOfWeekEnum';
import { IdReference, OffsetPagedResults } from '~interfaces';
import { SearchPagedParameters } from '~interfaces/requests';
import {
	BaseReferenceResponse,
	SkcOffsetPagedResponse,
	SkcSingleResponse,
} from '~interfaces/responses';
import { TopologyProxiedSkopeiConnectService } from '~services';
import { parseTime, time24Formatter } from '~utils/dateUtils';

import SkcSchedule, { SkcScheduleNew } from '../interfaces/skcSchedule';

/**
 * A service that does calls to the Skopei Connect API through
 * the Topology backend
 */
class SkcSchedulesService extends TopologyProxiedSkopeiConnectService {
	public readonly path = 'schedules';

	async getSchedules({
		page = 1,
		pageSize = 10,
		...args
	}: SearchPagedParameters): Promise<OffsetPagedResults<SkcSchedule>> {
		const { data } = await this._client.get<SkcOffsetPagedResponse<ScheduleResponse>>(this.path, {
			params: {
				'page-number': page,
				'page-size': pageSize,
				'organisation-id': args.organisationId,
				'search-query': args.searchQuery || undefined,
			},
		});

		return this.mapOffsetPagedResponse(data, SkcSchedulesService.fromResponse);
	}

	async getScheduleById(id: string): Promise<SkcSchedule> {
		const { data } = await this._client.get<SkcSingleResponse<ScheduleResponse>>(
			path.join(this.path, id)
		);

		return SkcSchedulesService.fromResponse(data.data);
	}

	async updateSchedule(id: string, data: SkcScheduleNew): Promise<null> {
		const content = SkcSchedulesService.toUpdateRequest(data);
		await this._client.put(path.join(this.path, id), content);

		return null;
	}

	async createSchedule(data: SkcScheduleNew): Promise<IdReference> {
		const content = SkcSchedulesService.toCreateRequest(data);

		const response = await this._client.post(this.path, content, {
			params: {
				'organisation-id': data.organisation.id,
			},
		});

		return this.mapIdResponse(response.data);
	}

	async deleteSchedule(id: string): Promise<null> {
		await this._client.delete(path.join(this.path, id));

		return null;
	}

	static toUpdateRequest(data: SkcScheduleNew): ScheduleUpdateRequest {
		const { label, description, schedule } = data;

		return {
			name: label,
			allDay: schedule.isOpenAllDay,
			openPeriodStart: time24Formatter.format(schedule.period?.start),
			openPeriodEnd: time24Formatter.format(schedule.period?.end),
			weekDays: schedule.daysOfWeek,
			description: description || undefined,
		};
	}

	static toCreateRequest(data: SkcScheduleNew): ScheduleCreateRequest {
		const { label, description, schedule, organisation } = data;

		return {
			name: label,
			allDay: schedule.isOpenAllDay,
			openPeriodStart: time24Formatter.format(schedule.period?.start),
			openPeriodEnd: time24Formatter.format(schedule.period?.end),
			weekDays: schedule.daysOfWeek,
			description: description || undefined,
			organisationId: organisation.id,
		};
	}

	static fromResponse(data: ScheduleResponse): SkcSchedule {
		const { id, name, allDay, weekDays, openPeriodEnd, openPeriodStart, organisation, ...rest } =
			data;

		return {
			...rest,
			id: id.toString(),
			label: name,
			schedule: {
				isOpenAllDay: allDay,
				...(openPeriodStart != null &&
					openPeriodEnd != null && {
						period: {
							start: parseTime(openPeriodStart),
							end: parseTime(openPeriodEnd),
						},
					}),
				daysOfWeek: weekDays as WeekdayEnum[],
			},
			// schedule: allDay && openPeriodStart != null && openPeriodEnd != null ? {
			// 	isOpenAllDay: false,
			// 	period: {
			// 		start: parseTime(openPeriodStart),
			// 		end: parseTime(openPeriodEnd),
			// 	},
			// 	daysOfWeek: weekDays as WeekdayEnum[],
			// } : {
			// 	isOpenAllDay: true,
			// 	daysOfWeek: weekDays as WeekdayEnum[],
			// },
			organisation: SkcSchedulesService.fromBaseReferenceResponse(organisation),
		};
	}
}

interface ScheduleUpdateRequest {
	name: string;
	description?: string;
	allDay: boolean;
	openPeriodStart?: string; // xx:xx:xx
	openPeriodEnd?: string; // xx:xx:xx
	weekDays: ('sunday' | 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday')[];
}

interface ScheduleCreateRequest extends ScheduleUpdateRequest {
	organisationId: string;
}

interface ScheduleResponse extends ScheduleUpdateRequest, BaseReferenceResponse {
	organisation: BaseReferenceResponse;
}

export default SkcSchedulesService;
